// Pure helpers for the Reporting page. No React, no Redux.

export const DEFAULT_WIDGET_SETTINGS = {
  title: "",
  yTitle: "",
  dataLabels: true,
  span: 12, // 12 = half width, 24 = full width
  periods: [], // period keys to show; empty = all years
  hideSelection: false, // hide the series pickers, show only the chart/table
  tableLayout: "series", // tables only, see TABLE_LAYOUTS
  compareTo: null, // grouped tables: base source key ("bc:<id>"/"sc:<id>") for Δ columns
  compareShow: "both", // tables: "both" | "abs" | "pct"
  chartCompare: "valuesAndDiff", // charts: see CHART_COMPARE_OPTIONS
  customColumns: [], // tables with the custom layout: see CUSTOM_COLUMN_TYPES
  pivot: null, // tables with the pivot layout: { rows, columns }, see normalizePivot (null = DEFAULT_PIVOT)
  periodCalcs: [], // tables with the pivot layout: changes and CAGRs between periods, see PERIOD_CALC_TYPES
  transpose: false, // tables other than pivots: swap rows and columns
  format: {},
};

export const EMPTY_SERIES_FIELDS = {
  business_case_id: -1,
  scenario_id: -1,
  account_id: -1,
  associated_account_id: -1,
  tab_name: "",
  account_number: "",
};

export const toId = (v) => {
  if (v === null || v === undefined || v === "") return -1;
  const n = Number(v);
  return Number.isFinite(n) ? n : -1;
};

// ---------------------------------------------------------------------------
// Widgets
// ---------------------------------------------------------------------------
export function parseSettings(raw) {
  let parsed = raw;
  if (typeof raw === "string") {
    try {
      parsed = raw ? JSON.parse(raw) : {};
    } catch {
      parsed = {};
    }
  }
  const obj = parsed && typeof parsed === "object" ? parsed : {};
  return { ...DEFAULT_WIDGET_SETTINGS, ...obj, format: { ...(obj.format || {}) } };
}

export function normalizeWidget(widget) {
  return {
    ...widget,
    settings: parseSettings(widget.settings),
    series: [...(widget.series || [])]
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.series_id - b.series_id)
      .map((s) => ({ ...s, style: parseSeriesStyle(s.style) })),
  };
}

// ---------------------------------------------------------------------------
// Row formatting (tables with one row per series or custom columns)
// ---------------------------------------------------------------------------
// series.style = { kind, text, label, bold, italic, indent, lineAbove, decimals }
//   kind:     "account" (default) | "blank" (empty row) | "heading" (text only, in `text`)
//   label:    replaces an account row's generated label
//   decimals: decimals for the row's amounts; null = the table's
export const ROW_KINDS = { account: "account", blank: "blank", heading: "heading" };
export const MAX_ROW_INDENT = 4;
export const MAX_ROW_DECIMALS = 6;

export function parseSeriesStyle(raw) {
  let parsed = raw;
  if (typeof raw === "string") {
    try {
      parsed = raw ? JSON.parse(raw) : {};
    } catch {
      parsed = {};
    }
  }
  const obj = parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  const indent = Number(obj.indent);
  const decimals = obj.decimals == null || obj.decimals === "" ? NaN : Number(obj.decimals);
  return {
    kind: Object.values(ROW_KINDS).includes(obj.kind) ? obj.kind : ROW_KINDS.account,
    text: typeof obj.text === "string" ? obj.text : "",
    label: typeof obj.label === "string" ? obj.label : "",
    bold: !!obj.bold,
    italic: !!obj.italic,
    indent: Number.isInteger(indent) ? Math.min(Math.max(indent, 0), MAX_ROW_INDENT) : 0,
    lineAbove: !!obj.lineAbove,
    decimals: Number.isInteger(decimals) ? Math.min(Math.max(decimals, 0), MAX_ROW_DECIMALS) : null,
  };
}

// What is stored: only the fields that differ from a plain account row.
export function compactSeriesStyle(style) {
  const s = parseSeriesStyle(style);
  const differs = (k, v) => (k === "kind" ? v !== ROW_KINDS.account : k === "decimals" ? v != null : !!v);
  return Object.fromEntries(Object.entries(s).filter(([k, v]) => differs(k, v)));
}

// Blank and heading rows: no account, no data.
export const isSpacerRow = (row) => {
  const kind = row?.style?.kind;
  return kind === ROW_KINDS.blank || kind === ROW_KINDS.heading;
};

// Row formatting only applies where one table row is one series (or account)
// in the series' order.
export const supportsRowStyles = (widget) => {
  if (widget?.type !== "table") return false;
  const { layout, transpose } = getTableOptions(widget);
  return !transpose && (layout === TABLE_LAYOUTS.series || layout === TABLE_LAYOUTS.custom);
};

// Shape sent to the API when a widget changes (settings go back as a JSON string).
export function toWidgetPayload(widget, patch = {}) {
  const next = { ...widget, ...patch };
  return {
    widget_id: next.widget_id,
    type: next.type,
    position: next.position,
    settings: JSON.stringify(next.settings ?? DEFAULT_WIDGET_SETTINGS),
  };
}

// ---------------------------------------------------------------------------
// Periods
// ---------------------------------------------------------------------------
// A period's key is its `period` string: "2025" (year), "2025-Q1" (quarter)
// or "2025-3" (month). Quarters and months only exist for expanded years.
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const isBlank = (x) => x == null || String(x).trim() === "";

const getPeriodKind = (p) =>
  !isBlank(p?.period_month) ? "month" : !isBlank(p?.period_quarter) ? "quarter" : "year";

function getPeriodLabel(p) {
  const year = String(p.period_year ?? p.period);
  const kind = getPeriodKind(p);
  if (kind === "month") return `${MONTH_NAMES[Number(p.period_month) - 1] ?? p.period_month} ${year}`;
  if (kind === "quarter") return `${p.period_quarter} ${year}`;
  return year;
}

// All periods as categories, in the organization's period order.
export const getPeriodCategories = (periods) =>
  (periods || []).map((p) => ({
    key: String(p.period),
    label: getPeriodLabel(p),
    kind: getPeriodKind(p),
    year: String(p.period_year ?? p.period),
  }));

// The widget's selected periods, in period order. Falls back to all years when
// nothing is selected or none of the selected periods exist any more.
export function getWidgetPeriods(periodCats, widget) {
  const selected = new Set((widget?.settings?.periods || []).map(String));
  const filtered = periodCats.filter((c) => selected.has(c.key));
  return filtered.length > 0 ? filtered : periodCats.filter((c) => c.kind === "year");
}

export function alignToPeriods(values, periodCats) {
  const byKey = new Map();
  for (const v of values || []) {
    // Some year rows carry a blank `period`; their year identifies them.
    const key = String(getPeriodKind(v) === "year" ? v.period_year ?? v.period ?? "" : v.period ?? "").trim();
    const n = Number(v.formula);
    if (key && Number.isFinite(n)) byKey.set(key, n);
  }
  return periodCats.map((c) => byKey.get(c.key) ?? 0);
}

// ---------------------------------------------------------------------------
// Scenarios
// ---------------------------------------------------------------------------
export function getFromScenarioMap(scenarioMap, sid) {
  if (toId(sid) === -1) return null;
  const key = String(sid);
  return (scenarioMap instanceof Map ? scenarioMap.get(key) : scenarioMap?.[key]) ?? null;
}

const hasEntries = (x) =>
  x instanceof Map ? x.size > 0 : !!x && typeof x === "object" && Object.keys(x).length > 0;

export const scenarioHasValues = (sc) =>
  !!sc && (hasEntries(sc.values) || hasEntries(sc.valuesById));

export const findScenarioName = (scenarioId, scenariosList) =>
  (scenariosList || []).find((s) => toId(s.scenario_id) === toId(scenarioId))?.name;

// ---------------------------------------------------------------------------
// Number formatting
// ---------------------------------------------------------------------------
export const SCALE_OPTIONS = [
  { value: 1, label: "Units" },
  { value: 1_000, label: "Thousands" },
  { value: 100_000, label: "Hundred thousands" },
  { value: 1_000_000, label: "Millions" },
];

const SIZING_DIVISORS = {
  Thousands: 1_000,
  HundredThousands: 100_000,
  Millions: 1_000_000,
};

export function buildGlobalFormat(organization) {
  return {
    locale: organization?.notation === "European" ? "da-DK" : "en-US",
    useGrouping: true,
    decimals: organization?.numericDecimals ?? 2,
    sizingDivisor: SIZING_DIVISORS[organization?.amountSizing] ?? 1,
    style: "decimal",
    currency: "DKK",
  };
}

export const getEffectiveFormat = (globalFmt, widget) => ({
  ...globalFmt,
  ...(widget?.settings?.format || {}),
});

const normalizeStyle = (fmt) => {
  const style = fmt?.style === "number" ? "decimal" : fmt?.style;
  if (style === "unit" && !fmt?.unit) return "decimal"; // Intl throws without a unit
  return ["decimal", "currency", "percent", "unit"].includes(style) ? style : "decimal";
};

export function makeFormatter(fmt) {
  const decimals = Number.isFinite(fmt?.decimals) ? fmt.decimals : 2;
  const divisor = Number(fmt?.sizingDivisor) || 1;
  const style = normalizeStyle(fmt);

  const options = {
    style,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: fmt?.useGrouping ?? true,
  };
  if (style === "currency") options.currency = fmt?.currency || "DKK";
  if (style === "unit") options.unit = fmt.unit;

  // Build the Intl formatter once, not on every value.
  const nf = new Intl.NumberFormat(fmt?.locale || undefined, options);
  return (val) => {
    const n = Number(val);
    return Number.isFinite(n) ? nf.format(n / divisor) : "";
  };
}

// For Δ % values, which are already percentages (5 = +5 %).
export function makePercentFormatter(fmt) {
  const nf = new Intl.NumberFormat(fmt?.locale || undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
    signDisplay: "exceptZero",
  });
  return (val) => {
    const n = Number(val);
    return val != null && Number.isFinite(n) ? `${nf.format(n)} %` : "";
  };
}

export function getDefaultYAxisTitle(fmt) {
  if (fmt?.style === "percent") return "Percent (%)";
  if (fmt?.style === "currency") return "Amount";
  const scale = SCALE_OPTIONS.find((o) => o.value === Number(fmt?.sizingDivisor));
  return !scale || scale.value === 1 ? "Amount" : `Amount (${scale.label.toLowerCase()})`;
}

// ---------------------------------------------------------------------------
// Tabs & accounts
// ---------------------------------------------------------------------------
const BUILT_IN_TABS = {
  Assets: (s) => s?.balancesheet?.assets,
  Liabilities: (s) => s?.balancesheet?.liabilities,
  Equities: (s) => s?.balancesheet?.equities,
  "Profit & Loss": (s) => s?.profitloss,
  "Cash Flow": (s) => s?.cashflow,
  Drivers: (s) => s?.drivers,
};

export const getTabNames = (accountStructure) => [
  ...Object.keys(BUILT_IN_TABS),
  ...(accountStructure?.custom || []).map((t) => t.name),
];

function flattenAccounts(nodes, out = []) {
  for (const n of nodes || []) {
    if (n?.account_number) out.push(n);
    if (n?.children) flattenAccounts(n.children, out);
  }
  return out;
}

export function getAccountsForTab(accountStructure, tabName) {
  if (!accountStructure || !tabName) return [];
  if (BUILT_IN_TABS[tabName]) return flattenAccounts(BUILT_IN_TABS[tabName](accountStructure));

  const custom = (accountStructure.custom || []).find((t) => t.name === tabName);
  return (custom?.custom_tab_accounts || []).map((cta) => cta?.account).filter(Boolean);
}

// A series' business case or scenario as "bc:<id>" / "sc:<id>", or undefined.
export const sourceKeyOf = (row) => {
  if (toId(row?.business_case_id) !== -1) return `bc:${row.business_case_id}`;
  if (toId(row?.scenario_id) !== -1) return `sc:${row.scenario_id}`;
  return undefined;
};

// account_id alone is not unique once associated accounts are involved.
export const accountKey = (a) => `${toId(a?.account_id)}|${toId(a?.associated_account_id)}`;

// ---------------------------------------------------------------------------
// Series → chart data
// ---------------------------------------------------------------------------
const seriesLabel = (source, account) => (account ? `${source} – ${account}` : source);

const seriesResult = (row, sourceKey, sourceName, accountName, values, periodCats) => ({
  key: row.series_id,
  name: seriesLabel(sourceName, accountName),
  sourceKey,
  sourceName,
  accountKey: accountKey(row),
  accountName: accountName || row.account_number,
  data: alignToPeriods(values, periodCats),
});

// ---------------------------------------------------------------------------
// Table layouts
// ---------------------------------------------------------------------------
export const TABLE_LAYOUTS = {
  series: "series", // one row per series, one column per period
  periodSource: "periodSource", // one row per account; periods, each split by source
  sourcePeriod: "sourcePeriod", // one row per account; sources, each split by period
  pivot: "pivot", // business case/scenario, account and period each in rows or columns, see settings.pivot
  custom: "custom", // one row per account; columns chosen one by one, see CUSTOM_COLUMN_TYPES
};

export const TABLE_LAYOUT_OPTIONS = [
  { value: TABLE_LAYOUTS.series, label: "One row per series" },
  { value: TABLE_LAYOUTS.periodSource, label: "Periods, then business case/scenario" },
  { value: TABLE_LAYOUTS.sourcePeriod, label: "Business case/scenario, then periods" },
  { value: TABLE_LAYOUTS.pivot, label: "Choose rows and columns (business case/scenario, account, period)" },
  { value: TABLE_LAYOUTS.custom, label: "Custom columns (business case/scenario and period per column, changes and CAGR)" },
];

// ---------------------------------------------------------------------------
// Pivot: where business case/scenario, account and period go
// ---------------------------------------------------------------------------
// settings.pivot = { rows: [dimension], columns: [dimension] }. Every
// dimension is in exactly one of them; the first is the outermost.
export const PIVOT_DIMENSIONS = ["source", "account", "period"];

export const DIMENSION_LABELS = {
  source: "Business case/scenario",
  account: "Account",
  period: "Period",
};

export const DEFAULT_PIVOT = { rows: ["account"], columns: ["period", "source"] };

// The grouped layouts are pivots with a fixed placement.
const LAYOUT_PIVOTS = {
  periodSource: { rows: ["account"], columns: ["period", "source"] },
  sourcePeriod: { rows: ["account"], columns: ["source", "period"] },
};

/**
 * A valid pivot: unknown and repeated dimensions dropped, missing ones added
 * to the columns, and at least one dimension in rows and in columns.
 */
export function normalizePivot(pivot = DEFAULT_PIVOT) {
  if (!pivot) pivot = DEFAULT_PIVOT;
  const seen = new Set();
  const keep = (list) =>
    (Array.isArray(list) ? list : []).filter((d) => PIVOT_DIMENSIONS.includes(d) && !seen.has(d) && seen.add(d));
  const rows = keep(pivot?.rows);
  const columns = keep(pivot?.columns);
  PIVOT_DIMENSIONS.forEach((d) => !seen.has(d) && columns.push(d));
  if (rows.length === 0) rows.push(columns.pop());
  if (columns.length === 0) columns.push(rows.pop());
  return { rows, columns };
}

// settings.periodCalcs (pivot tables): changes and CAGRs between two periods,
// shown after the periods wherever the period dimension is:
//   { id, type: "change", from: "<period key>", to: "<period key>", show: "pct" | "abs", label? }
//   { id, type: "cagr",   from: "<period key>", to: "<period key>", label? }
export const PERIOD_CALC_TYPES = { change: "change", cagr: "cagr" };

/**
 * periodCalcs with their header, kind and apply(data) → value, where data is
 * aligned to periodCats. A calculation with an unknown period gives null.
 */
export function resolvePeriodCalcs(periodCats, periodCalcs) {
  const index = new Map(periodCats.map((c, i) => [c.key, i]));
  const labelOf = (key) => periodCats[index.get(key)]?.label ?? key ?? "?";
  return (periodCalcs || []).map((c) => {
    const isCagr = c.type === PERIOD_CALC_TYPES.cagr;
    const abs = !isCagr && c.show === "abs";
    const from = index.get(c.from);
    const to = index.get(c.to);
    const years = from == null || to == null ? 0 : periodPosition(periodCats[to]) - periodPosition(periodCats[from]);
    return {
      id: c.id,
      label: c.label || `${isCagr ? "CAGR" : abs ? "Δ" : "Δ %"} ${labelOf(c.from)} → ${labelOf(c.to)}`,
      kind: abs ? "diff" : "pct",
      apply: (data) => {
        if (from == null || to == null) return null;
        const a = data[from] ?? null;
        const b = data[to] ?? null;
        if (isCagr) return cagr(a, b, years);
        return abs ? difference(b, a) : percentChange(b, a);
      },
    };
  });
}

/**
 * The periods a pivot table needs data for: the shown ones plus those its
 * calculations use, in period order.
 */
export function getPivotDataPeriods(periodCats, shownCats, periodCalcs) {
  const keys = new Set([
    ...shownCats.map((c) => c.key),
    ...(periodCalcs || []).flatMap((c) => [c.from, c.to]).filter(Boolean),
  ]);
  return periodCats.filter((c) => keys.has(c.key));
}

export const COMPARE_SHOW_OPTIONS = [
  { value: "both", label: "Amount and %" },
  { value: "abs", label: "Amount" },
  { value: "pct", label: "%" },
];

// Layouts with one row or column per account (not per series), which can
// compare with a base business case/scenario.
export const isGroupedLayout = (layout) =>
  layout === TABLE_LAYOUTS.periodSource || layout === TABLE_LAYOUTS.sourcePeriod || layout === TABLE_LAYOUTS.pivot;

// Only tables can be grouped or compared; charts always have one series per
// line/bar. compareTo is a source key ("bc:<id>" / "sc:<id>") or null.
// transpose swaps rows and columns (not for pivots, which place them freely).
export const getTableOptions = (widget) => {
  const s = widget?.settings || {};
  const isTable = widget?.type === "table";
  const layout = isTable ? s.tableLayout ?? TABLE_LAYOUTS.series : TABLE_LAYOUTS.series;
  return {
    layout,
    compareTo: isGroupedLayout(layout) ? s.compareTo || null : null,
    compareShow: s.compareShow || "both",
    pivot: layout === TABLE_LAYOUTS.pivot ? normalizePivot(s.pivot) : LAYOUT_PIVOTS[layout] ?? null,
    transpose: isTable && layout !== TABLE_LAYOUTS.pivot && !!s.transpose,
  };
};

// ---------------------------------------------------------------------------
// Chart comparison
// ---------------------------------------------------------------------------
export const CHART_COMPARE_OPTIONS = [
  { value: "valuesAndDiff", label: "Values and difference" },
  { value: "diff", label: "Difference only" },
  { value: "pct", label: "Difference in %" },
];

/**
 * The chart's comparison, or null. def is the widget type from the registry;
 * stacked charts can't mix values and differences (the stack total would be
 * meaningless), so they fall back to difference only.
 */
export function getChartCompare(widget, def) {
  const s = widget?.settings || {};
  if (def?.kind !== "chart" || !s.compareTo) return null;
  const mode = s.chartCompare || "valuesAndDiff";
  return { compareTo: s.compareTo, mode: def.stacked && mode === "valuesAndDiff" ? "diff" : mode };
}

/**
 * Adds or swaps in Δ series against the base source: one per other source and
 * account that the base also has. Δ series carry isDiff so charts can style
 * them. Returns the series unchanged when there's nothing to compare.
 */
export function applyChartCompare(series, compare) {
  if (!compare) return series;
  const baseByAccount = new Map();
  series.forEach((s) => {
    if (s.sourceKey === compare.compareTo && !baseByAccount.has(s.accountKey)) baseByAccount.set(s.accountKey, s);
  });
  if (baseByAccount.size === 0) return series;

  const calc = compare.mode === "pct" ? percentChange : difference;
  const diffs = series
    .filter((s) => s.sourceKey !== compare.compareTo && baseByAccount.has(s.accountKey))
    .map((s) => {
      const base = baseByAccount.get(s.accountKey);
      return {
        ...s,
        key: `diff:${s.key}`,
        name: `${compare.mode === "pct" ? "Δ %" : "Δ"} ${s.sourceName} vs ${base.sourceName} – ${s.accountName}`,
        isDiff: true,
        data: s.data.map((v, i) => calc(v, base.data[i])),
      };
    });

  return compare.mode === "valuesAndDiff" ? [...series, ...diffs] : diffs;
}

const uniqueBy = (list, key) => {
  const seen = new Map();
  list.forEach((x) => !seen.has(x[key]) && seen.set(x[key], x));
  return [...seen.values()];
};

const difference = (value, base) => (value == null || base == null ? null : value - base);

// Change in percent of the base (so 5 = +5 %). Empty when the base is 0.
const percentChange = (value, base) =>
  value == null || base == null || base === 0 ? null : ((value - base) / Math.abs(base)) * 100;

/*
 * Every table layout produces the same shape:
 *   rowTitles:    [string]  one per row-label level (the left header columns)
 *   columnTitles: [string]  one per column-header level (used when swapped)
 *   columns: [{ key, path: [string], kind }]  path = header labels, outermost
 *            first; neighbouring columns with the same outer labels share a
 *            spanning header
 *   rows:    [{ key, labels: [string], kind, values[] }]  values aligned to columns
 * kind is "value", "diff" (amount difference) or "pct" (a percentage, 5 = +5 %);
 * a cell is a difference/percentage when its column or its row says so.
 */
export const cellKind = (row, col) => (col.kind && col.kind !== "value" ? col.kind : row.kind || "value");

/**
 * Lays out resolved series as a table (shape above).
 * options: { layout, compareTo, compareShow, pivot, transpose } from getTableOptions
 */
export function buildTableLayout(series, periodCats, options = {}) {
  const table = isGroupedLayout(options.layout)
    ? buildPivotLayout(series, periodCats, options)
    : {
        rowTitles: ["Series"],
        columnTitles: ["Period"],
        columns: periodCats.map((c) => ({ key: c.key, path: [c.label], kind: "value" })),
        rows: series.map((s) => ({ key: s.key, labels: [s.name], kind: "value", values: s.data })),
      };
  return options.transpose ? transposeTable(table) : table;
}

/**
 * Business case/scenario, account and period placed in rows or columns as
 * options.pivot says, nested in that order. With a base (compareTo), the
 * business case/scenario dimension gets Δ / Δ % members after the others, so
 * they become columns or rows wherever that dimension is. Likewise the period
 * dimension gets the changes and CAGRs of periodCalcs after its periods. Rows
 * and columns for business case/scenario and account pairs that no series has
 * are left out.
 *
 * periodCats are the periods the series' data is aligned to; shownKeys the
 * ones shown (default: all). Calculations may use periods that aren't shown.
 */
function buildPivotLayout(
  series,
  periodCats,
  { pivot = DEFAULT_PIVOT, compareTo = null, compareShow = "both", shownKeys = null, periodCalcs = [] }
) {
  const { rows: rowDims, columns: columnDims } = normalizePivot(pivot);
  const sources = uniqueBy(series, "sourceKey");
  const accounts = uniqueBy(series, "accountKey");
  // First series wins if the same source/account pair was added twice.
  const lookup = new Map();
  series.forEach((s) => {
    const k = `${s.sourceKey}#${s.accountKey}`;
    if (!lookup.has(k)) lookup.set(k, s);
  });

  // The base only counts if it's actually in the table.
  const base = sources.find((s) => s.sourceKey === compareTo) ?? null;
  const compared = base ? sources.filter((s) => s !== base) : [];
  const kinds = [...(compareShow !== "pct" ? ["diff"] : []), ...(compareShow !== "abs" ? ["pct"] : [])];

  const members = {
    source: [
      ...sources.map((s) => ({
        key: s.sourceKey,
        label: s === base ? `${s.sourceName} (base)` : s.sourceName,
        at: { sourceKey: s.sourceKey, compare: "value" },
      })),
      ...compared.flatMap((s) =>
        kinds.map((kind) => ({
          key: `${kind}:${s.sourceKey}`,
          label: `${kind === "pct" ? "Δ %" : "Δ"} ${s.sourceName}`,
          at: { sourceKey: s.sourceKey, compare: kind },
          kind,
        }))
      ),
    ],
    account: accounts.map((a) => ({ key: a.accountKey, label: a.accountName, at: { accountKey: a.accountKey } })),
    period: [
      ...periodCats
        .map((c, i) => ({ key: c.key, label: c.label, at: { periodIndex: i } }))
        .filter((m) => !shownKeys || shownKeys.includes(m.key)),
      ...resolvePeriodCalcs(periodCats, periodCalcs).map((calc) => ({
        key: `calc:${calc.id}`,
        label: calc.label,
        at: { calc },
        kind: calc.kind,
      })),
    ],
  };

  // Every combination of the dimensions' members, outermost first. A
  // combination is a Δ or a percentage when one of its members is.
  const combine = (dims) =>
    dims.reduce((combos, dim) => combos.flatMap((combo) => members[dim].map((m) => [...combo, m])), [[]]);
  const describe = (combo) => ({
    key: combo.map((m) => m.key).join("|"),
    labels: combo.map((m) => m.label),
    at: Object.assign({}, ...combo.map((m) => m.at)),
    kind: combo.find((m) => m.kind)?.kind ?? "value",
  });
  const rowCombos = combine(rowDims).map(describe);
  const columnCombos = combine(columnDims).map(describe);

  // undefined = no series for this business case/scenario and account
  const cell = (row, col) => {
    const { sourceKey, accountKey, periodIndex, compare = "value", calc } = { ...row.at, ...col.at };
    const s = lookup.get(`${sourceKey}#${accountKey}`);
    if (!s) return undefined;
    // A change of a Δ isn't shown: it would mix percentages and percentage points.
    if (calc) return compare === "value" ? calc.apply(s.data) : null;
    const value = s.data[periodIndex] ?? null;
    if (compare === "value") return value;
    const baseValue = lookup.get(`${base.sourceKey}#${accountKey}`)?.data[periodIndex] ?? null;
    return compare === "diff" ? difference(value, baseValue) : percentChange(value, baseValue);
  };

  const grid = rowCombos.map((row) => columnCombos.map((col) => cell(row, col)));
  const keptRows = rowCombos.map((_, i) => i).filter((i) => grid[i].some((v) => v !== undefined));
  const keptColumns = columnCombos.map((_, j) => j).filter((j) => keptRows.some((i) => grid[i][j] !== undefined));

  return {
    rowTitles: rowDims.map((d) => DIMENSION_LABELS[d]),
    columnTitles: columnDims.map((d) => DIMENSION_LABELS[d]),
    columns: keptColumns.map((j) => {
      const c = columnCombos[j];
      return { key: c.key, path: c.labels, kind: c.kind };
    }),
    rows: keptRows.map((i) => {
      const r = rowCombos[i];
      return { key: r.key, labels: r.labels, kind: r.kind, values: keptColumns.map((j) => grid[i][j] ?? null) };
    }),
  };
}

// Rows become columns and the other way round.
export function transposeTable(table) {
  return {
    rowTitles: table.columnTitles,
    columnTitles: table.rowTitles,
    columns: table.rows.map((r) => ({ key: r.key, path: r.labels, kind: r.kind ?? "value" })),
    rows: table.columns.map((c, j) => ({
      key: c.key,
      labels: c.path,
      kind: c.kind ?? "value",
      values: table.rows.map((r) => r.values[j]),
    })),
  };
}

// ---------------------------------------------------------------------------
// Custom columns
// ---------------------------------------------------------------------------
// settings.customColumns, in display order:
//   { id, type: "value",  source: "bc:<id>" | "sc:<id>", period: "<period key>", label? }
//   { id, type: "change", from: <id>, to: <id>, show: "pct" | "abs", label? }
//   { id, type: "cagr",   from: <id>, to: <id>, label? }
// from/to are the ids of value columns. label replaces the generated header.
export const CUSTOM_COLUMN_TYPES = { value: "value", change: "change", cagr: "cagr" };

export const nextColumnId = (columns) =>
  (columns || []).reduce((m, c) => Math.max(m, Number(c.id) || 0), 0) + 1;

// Custom-column tables only use their series' accounts; the columns choose the
// business case/scenario.
export const isCustomTable = (widget) => getTableOptions(widget).layout === TABLE_LAYOUTS.custom;

// The business cases/scenarios a widget needs loaded: its series' and, for a
// custom-column table, the ones its columns use.
export function widgetSourceKeys(widget) {
  const keys = new Set((widget?.series || []).map(sourceKeyOf).filter(Boolean));
  if (isCustomTable(widget)) {
    (widget.settings.customColumns || []).forEach((c) => c.type === "value" && c.source && keys.add(c.source));
  }
  return [...keys];
}

// A business case's or scenario's name from its source key.
export function sourceNameOf(sourceKey, ctx) {
  for (const group of ctx?.sourceOptions || []) {
    const hit = (group.options || []).find((o) => o.value === sourceKey);
    if (hit) return hit.label;
  }
  const [kind, id] = String(sourceKey ?? "").split(":");
  return kind === "bc" ? `Business case ${id}` : kind === "sc" ? `Scenario ${id}` : "";
}

// A period's position in years, so CAGR also works between quarters and
// months: "2025" → 2025, "2025-Q3" → 2025.5, "2025-4" → 2025.25.
export function periodPosition(cat) {
  const year = Number(cat?.year);
  if (!cat || !Number.isFinite(year)) return null;
  const sub = Number(String(cat.key).split("-")[1]?.replace(/^Q/i, ""));
  if (cat.kind === "quarter" && Number.isFinite(sub)) return year + (sub - 1) / 4;
  if (cat.kind === "month" && Number.isFinite(sub)) return year + (sub - 1) / 12;
  return year;
}

// Compound annual growth rate in percent (5 = +5 % a year). Empty when the
// values have different signs or one is 0 (CAGR isn't defined there), or when
// no time passes. Two negative values (e.g. costs) give the growth in size.
export function cagr(start, end, years) {
  if (start == null || end == null || start === 0 || !(years > 0)) return null;
  const ratio = end / start;
  return ratio > 0 ? (Math.pow(ratio, 1 / years) - 1) * 100 : null;
}

/**
 * Lays out a custom-column table (same shape as buildTableLayout, always
 * grouped). Value columns have the period as outer header and the source as
 * inner one, so neighbouring columns of the same year share a header.
 *
 *   accounts:  [{ key, name, row }]  one table row each
 *   columns:   settings.customColumns
 *   periodCats: all periods (columns may use any of them)
 *   getValues(sourceKey, account) → values aligned to periodCats, or null
 *   sourceName(sourceKey) → display name
 */
export function buildCustomTableLayout(accounts, customColumns, periodCats, { getValues, sourceName }) {
  const catIndex = new Map(periodCats.map((c, i) => [c.key, i]));
  const byId = new Map((customColumns || []).map((c) => [c.id, c]));
  const valueColumn = (id) => {
    const c = byId.get(id);
    return c?.type === CUSTOM_COLUMN_TYPES.value ? c : null;
  };
  const periodLabel = (key) => periodCats[catIndex.get(key)]?.label ?? key ?? "";
  const shortLabel = (c) => c.label || `${sourceName(c.source)} ${periodLabel(c.period)}`.trim();
  const rangeLabel = (c) => {
    const from = valueColumn(c.from);
    const to = valueColumn(c.to);
    return from && to ? `${shortLabel(from)} → ${shortLabel(to)}` : "Choose two columns";
  };

  const columns = (customColumns || []).map((c) => {
    if (c.type === CUSTOM_COLUMN_TYPES.change) {
      const abs = c.show === "abs";
      return { key: `c${c.id}`, group: abs ? "Δ" : "Δ %", label: c.label || rangeLabel(c), kind: abs ? "diff" : "pct", def: c };
    }
    if (c.type === CUSTOM_COLUMN_TYPES.cagr) {
      return { key: `c${c.id}`, group: "CAGR", label: c.label || rangeLabel(c), kind: "pct", def: c };
    }
    return {
      key: `c${c.id}`,
      group: periodLabel(c.period),
      label: c.label || sourceName(c.source),
      kind: "value",
      def: c,
    };
  });

  const rows = accounts.map((account) => {
    const bySource = new Map(); // one lookup per source and row
    const valueOf = (c) => {
      if (!c?.source || !catIndex.has(c.period)) return null;
      if (!bySource.has(c.source)) bySource.set(c.source, getValues(c.source, account));
      return bySource.get(c.source)?.[catIndex.get(c.period)] ?? null;
    };

    return {
      key: account.key,
      labels: [account.name],
      kind: "value",
      values: columns.map(({ def }) => {
        if (def.type === CUSTOM_COLUMN_TYPES.value) return valueOf(def);
        const from = valueColumn(def.from);
        const to = valueColumn(def.to);
        if (!from || !to) return null;
        if (def.type === CUSTOM_COLUMN_TYPES.cagr) {
          const years =
            periodPosition(periodCats[catIndex.get(to.period)]) - periodPosition(periodCats[catIndex.get(from.period)]);
          return cagr(valueOf(from), valueOf(to), years);
        }
        return def.show === "abs" ? difference(valueOf(to), valueOf(from)) : percentChange(valueOf(to), valueOf(from));
      }),
    };
  });

  return {
    rowTitles: ["Account"],
    // Levels mix periods with change/CAGR headers, so they have no title.
    columnTitles: ["", ""],
    columns: columns.map(({ key, group, label, kind }) => ({ key, path: [group, label], kind })),
    rows,
  };
}

// Value lookup for custom columns: the account's values in another business
// case or scenario, resolved like a series would be.
function customValuesGetter(ctx, periodCats) {
  return (sourceKey, account) => {
    const [kind, id] = String(sourceKey).split(":");
    const row = {
      ...account.row,
      business_case_id: kind === "bc" ? Number(id) : -1,
      scenario_id: kind === "sc" ? Number(id) : -1,
    };
    return resolveSeries(row, ctx, periodCats)?.data ?? null;
  };
}

/**
 * What a widget shows as a table (also used for Copy/Excel of charts, which
 * always use the series layout). series are the resolved series for the
 * widget's periods; custom-column tables use all of ctx.periodCats instead.
 */
export function buildWidgetTable(widget, ctx, periodCats, series) {
  const options = getTableOptions(widget);

  // Pivot calculations can use periods that aren't shown, so the series are
  // resolved again for those too.
  const periodCalcs = options.layout === TABLE_LAYOUTS.pivot ? widget.settings.periodCalcs || [] : [];
  if (periodCalcs.length > 0) {
    const dataCats = getPivotDataPeriods(ctx.periodCats, periodCats, periodCalcs);
    const dataSeries = (widget.series || []).map((row) => resolveSeries(row, ctx, dataCats)).filter(Boolean);
    return buildTableLayout(dataSeries, dataCats, {
      ...options,
      shownKeys: periodCats.map((c) => c.key),
      periodCalcs,
    });
  }

  const styled = supportsRowStyles(widget);

  if (options.layout !== TABLE_LAYOUTS.custom) {
    const table = buildTableLayout(series, periodCats, options);
    return styled ? applyRowStyles(table, widget.series, (r) => r.series_id) : table;
  }

  const accounts = uniqueBy(
    (widget.series || [])
      .filter((r) => r.account_number && !isSpacerRow(r))
      .map((r) => ({ key: accountKey(r), name: ctx.accountName(r) || r.account_number, row: r })),
    "key"
  );
  const table = buildCustomTableLayout(accounts, widget.settings.customColumns, ctx.periodCats, {
    getValues: customValuesGetter(ctx, ctx.periodCats),
    sourceName: (key) => sourceNameOf(key, ctx),
  });
  if (styled) return applyRowStyles(table, widget.series, accountKey);
  return options.transpose ? transposeTable(table) : table;
}

/**
 * Puts a table's rows in the series' order with blank and heading rows in
 * between, and gives each row its series' style. keyOf(series) is the key of
 * the table row a series makes; the first series with a key decides its style.
 * Rows get `style` and, for blank/heading rows, `rowType`.
 */
export function applyRowStyles(table, seriesRows, keyOf) {
  const byKey = new Map(table.rows.map((r) => [r.key, r]));
  const noValues = table.columns.map(() => null);
  const used = new Set();
  const rows = [];

  (seriesRows || []).forEach((s) => {
    const style = s.style || parseSeriesStyle(null);
    if (isSpacerRow(s)) {
      const label = style.kind === ROW_KINDS.heading ? style.text : "";
      rows.push({ key: `row:${s.series_id}`, labels: [label], kind: "value", values: noValues, rowType: style.kind, style });
      return;
    }
    const key = keyOf(s);
    const row = byKey.get(key);
    if (!row || used.has(key)) return;
    used.add(key);
    rows.push({ ...row, labels: style.label ? [style.label] : row.labels, style });
  });

  return { ...table, rows };
}

/**
 * A table (from buildWidgetTable) as rows of cells for Copy and Excel: one
 * header row per column-header level (outer labels repeated on every column
 * so Excel filters work) and one label cell per row-label level, repeated on
 * every row. Values are full amounts (not scaled to thousands/millions) so
 * they can be calculated with in Excel; Δ % and CAGR are percentages (5 = +5 %).
 */
export function tableExportRows({ rowTitles, columns, rows }) {
  const levels = Math.max(1, ...columns.map((c) => c.path.length));
  const header = Array.from({ length: levels }, (_, level) => [
    ...rowTitles.map((title) => (level === levels - 1 ? title : "")),
    ...columns.map((c) => c.path[level] ?? ""),
  ]);
  return [...header, ...rows.map((r) => [...r.labels, ...r.values])];
}

/**
 * Turns one reporting_series row into { key, name, data[] } aligned to the
 * widget's periodCats, or null when the data isn't available (yet).
 *
 * ctx: { assembledById, scenarioMap, scenariosList, accountName(row) }
 */
export function resolveSeries(row, ctx, periodCats) {
  if (!row?.account_number || isSpacerRow(row)) return null;
  const accountName = ctx.accountName(row);

  const bcId = toId(row.business_case_id);
  if (bcId !== -1) {
    const bc = ctx.assembledById.get(bcId);
    if (!bc || bc.error) return null;

    const values =
      bc.assembledCase?.valuesById?.get?.(row.account_id) ??
      bc.assembledCase?.values?.get?.(row.account_number);
    if (values == null) return null;

    return seriesResult(row, `bc:${bcId}`, bc.name ?? `Business case ${bcId}`, accountName, values, periodCats);
  }

  const scId = toId(row.scenario_id);
  if (scId !== -1) {
    const sc = getFromScenarioMap(ctx.scenarioMap, scId);
    if (!sc) return null;

    const values =
      sc.valuesById?.get?.(row.account_id) ??
      sc.valuesById?.get?.(String(row.account_id)) ??
      sc.values?.get?.(row.account_number) ??
      sc.values?.[row.account_number];
    if (values == null) return null;

    const sourceName = findScenarioName(scId, ctx.scenariosList) ?? `Scenario ${scId}`;
    return seriesResult(row, `sc:${scId}`, sourceName, accountName, values, periodCats);
  }

  return null;
}